/**
 * Regional profile — the single source of truth for regional behavior.
 *
 * The app resolves one active region for the signed-in account. The users API
 * supplies it at runtime, a session cache preserves it across reloads, and the
 * US profile is the safe fallback while neither is available. Regional
 * behavior therefore comes from the active region, never from UI language.
 *
 * THE INVARIANT THIS MODULE EXISTS TO ENFORCE
 *
 *   UI locale must never determine measurement system, name order, or
 *   contact-field behavior.
 *
 * Before this module, `locale === "ja"` selected metric units and Japanese
 * contact rules, and `locale === "en"` selected First-Last name order. That
 * worked only while English and Japanese were the sole shipping languages. The
 * moment a market runs a language other than its own — US in Spanish, Canada in
 * French — locale-driven rules give the wrong units and reversed names.
 *
 * Consumers must call the helpers below rather than branch on market or locale
 * themselves, so that a screen added later inherits correct behavior without
 * another architectural change.
 *
 * TARGET vs ENABLED LOCALES
 *
 * `allowedLocales` is the target matrix a market will eventually offer.
 * `enabledLocales` is what the running app may actually select today. They are
 * separate because a locale is only shippable once its translations exist, and
 * translation is Stage 2. Every runtime path resolves against `enabledLocales`;
 * `allowedLocales` exists so the target matrix stays visible and testable.
 */
import { getCurrentLocale } from "../utlis/locale";

/* ------------------------------------------------------------------ market */

/**
 * Fallback used before an API or cached region is available. It is deliberately
 * not inferred from language, timezone, phone prefix, or IP.
 */
export const ACTIVE_MARKET = "US";

export const MARKETS = ["US", "JP", "IN", "ME", "CA"];

/**
 * Languages no market may offer. `ru.json` is legacy: it exists in the repo but
 * is not reachable from any customer configuration, and US in particular is
 * EN / ES / ZH only. Enforced below rather than left to convention.
 */
export const RETIRED_LOCALES = ["ru"];

/* ---------------------------------------------------------------- profiles */

/**
 * measurementSystem  "imperial" | "metric"  — UI display units only. The API
 *                    and storage remain cm/kg in every market.
 * nameOrder          "first_last" | "last_first"
 * contactRules       key into CONTACT_RULES below
 * allowedLocales     target matrix: UI languages this market will offer
 * enabledLocales     subset of the above that is shippable today (Stage 1)
 * defaultLocale      used when the requested locale is not allowed
 */
const PROFILES = {
  US: {
    market: "US",
    allowedLocales: ["en", "zh", "es"],
    // Stage 2 complete: zh and es reach full key parity with en.
    enabledLocales: ["en", "zh", "es"],
    defaultLocale: "en",
    measurementSystem: "imperial",
    nameOrder: "first_last",
    contactRules: "US",
  },
  JP: {
    market: "JP",
    allowedLocales: ["ja"],
    enabledLocales: ["ja"],
    defaultLocale: "ja",
    measurementSystem: "metric",
    nameOrder: "last_first",
    contactRules: "JP",
  },
  IN: {
    market: "IN",
    allowedLocales: ["en"],
    enabledLocales: ["en"],
    defaultLocale: "en",
    measurementSystem: "metric",
    nameOrder: "first_last",
    contactRules: "IN",
  },
  ME: {
    market: "ME",
    allowedLocales: ["en"],
    enabledLocales: ["en"],
    defaultLocale: "en",
    measurementSystem: "metric",
    nameOrder: "first_last",
    contactRules: "ME",
  },
  CA: {
    market: "CA",
    allowedLocales: ["fr", "en"],
    // Stage 2 complete: fr reaches full key parity with en.
    enabledLocales: ["fr", "en"],
    // Ultimately customer-configurable per Canadian build; a Canadian customer
    // build may set this to "en". Both are enabled and both are metric.
    defaultLocale: "fr",
    measurementSystem: "metric",
    nameOrder: "first_last",
    contactRules: "CA",
  },
};

/* ---------------------------------------------------------- contact rules */

const digitsOnly = (value) => String(value || "").replace(/\D/g, "");

// Keeps letters, digits and single spaces; used where a postal code is not
// numeric. Non-destructive: it never reorders, uppercases, or reformats.
const alphanumericAndSpace = (value) =>
  String(value || "").replace(/[^A-Za-z0-9 ]/g, "");

/**
 * Postal and phone are optional profile fields. They are collected in Edit
 * only, never in Add, so they cannot gate registration in any market.
 *
 * `sanitizePostal` / `sanitizePhone` are what the input handlers call, so a
 * market whose postal code contains letters keeps them as the user types.
 */
const CONTACT_RULES = {
  // Unchanged from v92.
  JP: {
    postalMaxLength: 7,
    phoneMaxLength: 11,
    isValidPostal: (value) => /^\d{7}$/.test(digitsOnly(value)),
    isValidPhone: (value) => /^\d{10,11}$/.test(digitsOnly(value)),
    sanitizePostal: (value) => digitsOnly(value).slice(0, 7),
    sanitizePhone: (value) => digitsOnly(value).slice(0, 11),
    postalInputMode: "numeric",
  },
  // Unchanged from v92.
  US: {
    postalMaxLength: 5,
    phoneMaxLength: 10,
    isValidPostal: (value) => /^\d{5}$/.test(digitsOnly(value)),
    isValidPhone: (value) => /^\d{10}$/.test(digitsOnly(value)),
    sanitizePostal: (value) => digitsOnly(value).slice(0, 5),
    sanitizePhone: (value) => digitsOnly(value).slice(0, 10),
    postalInputMode: "numeric",
  },
  // India: PIN is 6 digits, mobile is 10. Same shape as US, no special casing.
  IN: {
    postalMaxLength: 6,
    phoneMaxLength: 10,
    isValidPostal: (value) => /^\d{6}$/.test(digitsOnly(value)),
    isValidPhone: (value) => /^\d{10}$/.test(digitsOnly(value)),
    sanitizePostal: (value) => digitsOnly(value).slice(0, 6),
    sanitizePhone: (value) => digitsOnly(value).slice(0, 10),
    postalInputMode: "numeric",
  },
  // Canada: postal codes contain letters. "A1A 1A1" and "A1A1A1" both valid,
  // with or without the space. digitsOnly would destroy them, so postal uses
  // the alphanumeric sanitizer.
  CA: {
    postalMaxLength: 7,
    phoneMaxLength: 10,
    isValidPostal: (value) =>
      /^[A-Za-z]\d[A-Za-z] ?\d[A-Za-z]\d$/.test(String(value || "").trim()),
    isValidPhone: (value) => /^\d{10}$/.test(digitsOnly(value)),
    sanitizePostal: (value) => alphanumericAndSpace(value).slice(0, 7),
    sanitizePhone: (value) => digitsOnly(value).slice(0, 10),
    postalInputMode: "text",
  },
  // Middle East spans countries with different formats, and some with no
  // postal code at all. Deliberately permissive rather than inventing one
  // strict format: accept any short alphanumeric value, and accept blank.
  ME: {
    postalMaxLength: 12,
    phoneMaxLength: 15,
    isValidPostal: (value) => {
      const trimmed = String(value || "").trim();
      return trimmed === "" || /^[A-Za-z0-9 ]{3,12}$/.test(trimmed);
    },
    isValidPhone: (value) => /^\d{7,15}$/.test(digitsOnly(value)),
    sanitizePostal: (value) => alphanumericAndSpace(value).slice(0, 12),
    sanitizePhone: (value) => digitsOnly(value).slice(0, 15),
    postalInputMode: "text",
  },
};

/* --------------------------------------------------- runtime active region */

/**
 * The region the running app resolves against. It is populated from the
 * logged-in user's `region` block by the common utility in
 * `configs/activeRegion.js`, so the market — and therefore units, name order
 * and contact rules — comes from the backend rather than a build constant.
 *
 * It is `null` until that block arrives (first paint, logged out, or a backend
 * that has not shipped the block yet). While null every resolver falls back to
 * `ACTIVE_MARKET` ("US"), so today's behavior is preserved. Add, Edit and the
 * bulk-import Review all read units through `getRegionalProfile()` with no
 * explicit market, so setting this once drives every flow identically.
 */
let runtimeProfile = null;

const isMeasurementSystem = (value) =>
  value === "metric" || value === "imperial";
const isNameOrder = (value) => value === "first_last" || value === "last_first";
const cleanLocaleList = (list) =>
  Array.isArray(list)
    ? [
        ...new Set(
          list
            .map((locale) =>
              String(locale || "")
                .trim()
                .toLowerCase(),
            )
            .filter((locale) => locale && !RETIRED_LOCALES.includes(locale)),
        ),
      ]
    : null;

/** The active market, API-driven when set, otherwise the build default. */
export const getActiveMarket = () => runtimeProfile?.market || ACTIVE_MARKET;

/**
 * Make a normalized region (from `activeRegion.js`) the active region.
 *
 * The market defaults to "US" when it is missing or unknown — the app must
 * keep working when the backend omits the block. Every other field overrides
 * the built-in profile for that market only when a valid value is present;
 * when absent, the built-in value stands (so US and JP behave exactly as the
 * static profiles define them). Storage and the API remain cm/kg regardless;
 * `measurementSystem` changes display units only. Returns the resolved profile.
 */
export const setActiveRegion = (region) => {
  const market =
    region && PROFILES[region.market] ? region.market : ACTIVE_MARKET;
  const resolved = { ...PROFILES[market], market };
  if (region) {
    if (isMeasurementSystem(region.measurementSystem)) {
      resolved.measurementSystem = region.measurementSystem;
    }
    if (isNameOrder(region.nameOrder)) {
      resolved.nameOrder = region.nameOrder;
    }
    if (region.contactRules && CONTACT_RULES[region.contactRules]) {
      resolved.contactRules = region.contactRules;
    }
    const allowed = cleanLocaleList(region.allowedLocales);
    const enabled = cleanLocaleList(region.enabledLocales);
    if (allowed?.length) resolved.allowedLocales = allowed;
    if (enabled?.length) resolved.enabledLocales = enabled;
    if (region.defaultLocale) {
      resolved.defaultLocale = String(region.defaultLocale)
        .trim()
        .toLowerCase();
    }
  }
  runtimeProfile = resolved;
  return resolved;
};

/** Clear the runtime region (logout, tests); resolvers fall back to US. */
export const resetActiveRegion = () => {
  runtimeProfile = null;
};

/* --------------------------------------------------------------- resolvers */

/**
 * Resolves a market to its profile.
 *
 * With no explicit market it returns the runtime active region (API-driven) if
 * one is set, otherwise the build default `ACTIVE_MARKET`. The API path
 * deliberately falls back to "US" rather than failing, because a logged-out or
 * not-yet-loaded app must still render.
 *
 * An explicit, *unknown* market still fails loudly — a build or a per-row
 * import call that names a bogus market is a bug, not something to paper over
 * by shipping pounds to Japan.
 */
export const getRegionalProfile = (market) => {
  if (market == null) {
    if (runtimeProfile) return runtimeProfile;
    market = ACTIVE_MARKET;
  }
  const profile = PROFILES[market];
  if (profile) {
    const retired = profile.enabledLocales.filter((l) =>
      RETIRED_LOCALES.includes(l),
    );
    if (retired.length) {
      throw new Error(
        `Market "${market}" enables retired locale(s): ${retired.join(", ")}.`,
      );
    }
  }
  if (!profile) {
    throw new Error(
      `Unknown market "${market}". Expected one of: ${MARKETS.join(", ")}. ` +
        "Set the market in the customer build configuration.",
    );
  }
  return profile;
};

export const getMeasurementSystem = (market) =>
  getRegionalProfile(market).measurementSystem;

export const usesMetricMeasurementSystem = (market) =>
  getMeasurementSystem(market) === "metric";

export const getNameOrder = (market) => getRegionalProfile(market).nameOrder;

export const getContactRules = (market) =>
  CONTACT_RULES[getRegionalProfile(market).contactRules];

/** Target matrix — what this market will eventually offer. */
export const getAllowedLocales = (market) =>
  getRegionalProfile(market).allowedLocales.slice();

/** Shippable today. Every runtime decision uses this, not allowedLocales. */
export const getEnabledLocales = (market) =>
  getRegionalProfile(market).enabledLocales.slice();

/** Target default, which may not yet be enabled (CA "fr" during Stage 1). */
export const getDefaultLocale = (market) =>
  getRegionalProfile(market).defaultLocale;

/**
 * The default the running app actually uses: the target default when it is
 * enabled, otherwise the first enabled locale. A profile with nothing enabled
 * is a configuration error and throws rather than resolving to nothing.
 */
export const getEffectiveDefaultLocale = (market) => {
  const profile = getRegionalProfile(market);
  if (profile.enabledLocales.includes(profile.defaultLocale)) {
    return profile.defaultLocale;
  }
  if (!profile.enabledLocales.length) {
    throw new Error(
      `Market "${profile.market}" has no enabled locale. Enable at least one.`,
    );
  }
  return profile.enabledLocales[0];
};

export const isLocaleAllowed = (locale, market) =>
  getRegionalProfile(market).allowedLocales.includes(String(locale || ""));

export const isLocaleEnabled = (locale, market) =>
  getRegionalProfile(market).enabledLocales.includes(String(locale || ""));

/**
 * THE single locale-resolution path.
 *
 * Every source of a locale is untrusted and goes through here: a shell-injected
 * `locale` URL parameter, a locale restored from a stored session written by an
 * older build, a saved preference, or a browser language. Anything not enabled
 * for this market resolves to the effective default.
 *
 * Resolving against `enabledLocales` rather than `allowedLocales` is what keeps
 * unfinished Stage 2 languages out of the running app: zh, es and fr are in the
 * target matrix but cannot become the active locale until Stage 2 enables them.
 */
export const resolveLocale = (requestedLocale, market) => {
  const profile = getRegionalProfile(market);
  return profile.enabledLocales.includes(String(requestedLocale || ""))
    ? String(requestedLocale)
    : getEffectiveDefaultLocale(market);
};

/**
 * Key the session is stored under. Owned here rather than in mmtFlow so that
 * the locale precedence below can read it without importing mmtFlow, which
 * would create an import cycle. mmtFlow re-exports it for existing callers.
 */
export const SESSION_STORAGE_KEY = "mmtSession";

/** Locale recorded on the stored session, or "" if there is none to read. */
const storedSessionLocale = () => {
  try {
    const raw =
      globalThis.sessionStorage?.getItem(SESSION_STORAGE_KEY) ||
      globalThis.localStorage?.getItem(SESSION_STORAGE_KEY);
    return JSON.parse(raw || "null")?.locale || "";
  } catch {
    return "";
  }
};

/**
 * THE active locale for this build. One precedence, used everywhere:
 *
 *   1. the locale on the stored session, if this market enables it
 *   2. otherwise the app's locale source, clamped
 *   3. otherwise the market's effective default
 *
 * Step 1 is what makes a chosen language stick. Once Stage 2 enables zh or es
 * for a US build and a user selects one, it is written to the session, and
 * every later caller — API `Accept-Language`, navigation, month labels, legal
 * URLs — reads it back from there instead of silently reverting to whatever the
 * raw locale source happens to report.
 *
 * Callers must use this rather than getCurrentLocale(), so that no runtime path
 * can make a language decision from an unclamped or disabled locale.
 */
export const getActiveLocale = () => {
  const fromSession = storedSessionLocale();
  if (isLocaleEnabled(fromSession)) return String(fromSession);
  return resolveLocale(getCurrentLocale());
};
